import { useState } from "react";
import { Gift, HeartHandshake, Lock, BookOpen, Flower2, Droplet, Shirt, Star, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, formatError, imgUrl } from "@/lib/api";

export const DonateNow = ({ content }) => {
  const [contact, setContact] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/donations/start", { contact });
      window.location.assign(data.redirect_url);
    } catch (err) {
      setError(formatError(err));
      setLoading(false);
    }
  };

  return (
    <div id="support" className="relative scroll-mt-20 overflow-hidden rounded-lg border border-emerald-800/70 bg-emerald-950/30 p-5 sm:p-8" data-testid="donate-now-card">
      <HeartHandshake className="mb-4 h-8 w-8 text-emerald-400" strokeWidth={1.5} />
      <h3 className="font-display text-2xl text-gold">Already registered? Donate now</h3>
      <p className="mt-2 text-sm leading-relaxed text-neutral-300">{content.donation_intro}</p>
      <form onSubmit={submit} className="mt-5 space-y-3" data-testid="donate-now-form">
        <Input
          value={contact}
          onChange={(e) => setContact(e.target.value)}
          required
          placeholder="Email or phone you registered with"
          className="field-input"
          data-testid="donate-now-contact-input"
        />
        {error && (
          <p className="rounded-md border border-red-800 bg-red-950/50 p-3 text-sm text-red-300" data-testid="donate-now-error">
            {error}{" "}
            {/register/i.test(error) && <a href="#register" className="font-semibold text-amber-400 underline">Register here</a>}
          </p>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid="donate-now-button"
          className="flex h-14 w-full items-center justify-center gap-2 rounded-full bg-emerald-600 font-display text-lg font-semibold text-white shadow-[0_0_24px_rgba(21,128,61,0.35)] transition-transform duration-200 hover:-translate-y-0.5 hover:bg-emerald-500 disabled:opacity-60"
        >
          <Lock className="h-4 w-4" /> {loading ? "Opening secure page…" : "Donate now (min $5)"}
        </button>
        <p className="text-center text-xs text-neutral-500">Secure payment by Stripe. You choose the amount.</p>
      </form>
    </div>
  );
};

export const GiveawayRules = ({ content }) => (
  <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-5 sm:p-6" data-testid="giveaway-rules">
    <div className="mb-4 flex items-center gap-3">
      <Gift className="h-6 w-6 text-red-500" />
      <h3 className="font-display text-2xl text-neutral-50">Weekend giveaway rules</h3>
    </div>
    <ul className="space-y-3">
      {(content.giveaway_rules || "").split("\n").filter(Boolean).map((line, i) => (
        <li key={i} className="flex gap-3 text-sm leading-relaxed text-neutral-300">
          <span className="mt-2 h-1.5 w-1.5 shrink-0 rotate-45 bg-amber-500" />
          {line}
        </li>
      ))}
    </ul>
  </div>
);

const PRIZE_ICONS = { reading: BookOpen, wellness: Flower2, shea: Droplet, fabric: Shirt, gift: Gift, star: Star };
const PRIZE_TINTS = ["bg-red-800/80", "bg-emerald-800/80", "bg-amber-500/90 text-neutral-950", "bg-red-800/80", "bg-emerald-800/80"];

export const GiveawayPrizes = ({ content }) => {
  const prizes = content.prizes || [];
  if (!prizes.length) return null;
  return (
    <div data-testid="giveaway-prizes">
      <div className="mb-4 flex items-center gap-3">
        <Sparkles className="h-6 w-6 text-amber-400" />
        <h3 className="font-display text-2xl text-gold">Weekend giveaway prizes</h3>
      </div>
      <ul className="grid gap-3 sm:grid-cols-2">
        {prizes.map((p, i) => {
          const Icon = PRIZE_ICONS[p.icon] || Gift;
          const last = i === prizes.length - 1 && prizes.length % 2 === 1;
          return (
            <li
              key={i}
              data-testid={`prize-card-${i}`}
              className={`rise flex items-center gap-4 rounded-lg border border-neutral-800 bg-neutral-900 p-3 transition-transform duration-300 hover:-translate-y-0.5 ${last ? "sm:col-span-2" : ""}`}
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <div className={`flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-md ${p.image_id ? "bg-neutral-800" : PRIZE_TINTS[i % PRIZE_TINTS.length]}`}>
                {p.image_id ? (
                  <img src={imgUrl(`/api/images/${p.image_id}`)} alt={p.name} className="h-full w-full object-cover" />
                ) : (
                  <Icon className="h-7 w-7" strokeWidth={1.6} />
                )}
              </div>
              <div className="min-w-0">
                <p className="font-display text-lg leading-tight text-neutral-50">{p.name}</p>
                {p.description && <p className="mt-1 text-xs leading-relaxed text-neutral-400">{p.description}</p>}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
